import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Radio, Upload, Loader, CheckCircle, AlertTriangle } from 'lucide-react';
import { useDatabase } from '../../context/DatabaseContext';
import { scadaTagApi } from '../../services/api';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function triggerDownload(content, filename) {
  if (!content) return;
  let blob;
  if (filename?.endsWith('.xlsx')) {
    const bytes = Uint8Array.from(atob(content), c => c.charCodeAt(0));
    blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  } else {
    blob = new Blob([content], { type: 'text/plain' });
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ScadaTagGenerationPage() {
  const { dbSlug: slug } = useDatabase();

  const [system, setSystem] = useState('CIM');
  const [version, setVersion] = useState('');
  const [versions, setVersions] = useState([]);
  const [plcName, setPlcName] = useState('');

  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState('');
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState(null);
  const [generateError, setGenerateError] = useState('');

  // Load versions when system changes
  useEffect(() => {
    scadaTagApi.getVersions(system)
      .then(res => {
        const vList = res.data?.versions || [];
        setVersions(vList);
        setVersion(vList[0] || '');
      })
      .catch(() => {
        setVersions([]);
        setVersion('');
      });
  }, [system]);

  // Auto-download after generate
  useEffect(() => {
    if (!generateResult) return;
    triggerDownload(generateResult.fileA, generateResult.fileNameA);
    triggerDownload(generateResult.fileB, generateResult.fileNameB);
    if (generateResult.fileC) triggerDownload(generateResult.fileC, generateResult.fileNameC);
  }, [generateResult]);

  const handleFile = useCallback((f) => {
    if (!f) return;
    setFile(f);
    setFileName(f.name);
    setGenerateResult(null);
    setGenerateError('');
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }, [handleFile]);

  const handleGenerate = async () => {
    if (!file) { setGenerateError('Upload a CSV file first.'); return; }
    if (!plcName.trim()) { setGenerateError('PLC Name is required.'); return; }
    if (!version) { setGenerateError('Select a version.'); return; }

    setGenerating(true);
    setGenerateError('');
    setGenerateResult(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('system', system);
    formData.append('version', version);
    formData.append('plcName', plcName.trim());

    try {
      const res = await scadaTagApi.generate(slug, formData);
      setGenerateResult(res.data);
    } catch (err) {
      setGenerateError(err.response?.data || err.message || 'Generation failed.');
    } finally {
      setGenerating(false);
    }
  };

  const fileCount = 3;
  const fileLabels = system === 'CIM'
    ? ['CIM Import CSV (140 cols)', 'DataLog CSV', 'AlarmConfig XLSX']
    : ['IGN First Pass CSV', 'IGN Second Pass CSV', 'AlarmConfig XLSX'];

  return (
    <div className="dev-module import-template-page">
      <div className="dev-module-header">
        <div className="dev-module-header-left">
          <h1>
            <Radio size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            SCADA Tag Generation
          </h1>
          <span className="dev-module-meta">Tool 2 CSV &rarr; CIM Import / IGN Tag Files</span>
        </div>
      </div>

      <div className="dev-module-content">
        <div className="import-steps-layout" style={{ maxWidth: '1100px', alignItems: 'flex-start' }}>

          {/* ═══════════ TILE 1: CONFIGURE + UPLOAD ═══════════ */}
          <div className="import-step-panel" style={{ maxWidth: '420px' }}>
            <div className="import-step-header">
              <span className="import-step-number">1</span>
              <h3>SCADA System Configuration</h3>
            </div>

            {/* System toggle */}
            <div style={{ marginBottom: 'var(--spacing-md)' }}>
              <label style={labelStyle}>SCADA System</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {['CIM', 'IGN'].map(s => (
                  <button
                    key={s}
                    onClick={() => { setSystem(s); setGenerateResult(null); setGenerateError(''); }}
                    style={{
                      flex: 1,
                      padding: '8px 0',
                      border: `2px solid ${system === s ? 'var(--color-primary)' : 'var(--color-gray-200)'}`,
                      borderRadius: '4px',
                      background: system === s ? 'var(--color-primary)' : 'var(--color-bg-white)',
                      color: system === s ? '#fff' : 'var(--color-gray-700)',
                      fontWeight: 600,
                      fontSize: 'var(--text-sm)',
                      cursor: 'pointer',
                      transition: 'all 0.15s'
                    }}
                  >
                    {s === 'CIM' ? 'Cimplicity' : 'Ignition'}
                  </button>
                ))}
              </div>
            </div>

            {/* Version */}
            <div style={{ marginBottom: 'var(--spacing-md)' }}>
              <label style={labelStyle}>Version</label>
              <select
                value={version}
                onChange={e => setVersion(e.target.value)}
                style={inputStyle}
              >
                {versions.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>

            {/* PLC Name */}
            <div style={{ marginBottom: 'var(--spacing-md)' }}>
              <label style={labelStyle}>PLC / Device Name</label>
              <input
                type="text"
                value={plcName}
                onChange={e => setPlcName(e.target.value)}
                placeholder="e.g. FC1_PLC"
                style={{ ...inputStyle, boxSizing: 'border-box' }}
              />
            </div>

            {/* File upload */}
            <div style={{ marginBottom: 'var(--spacing-md)' }}>
              <label style={labelStyle}>Tool 2 Output CSV</label>
              <div
                className={`import-dropzone ${dragOver ? 'drag-over' : ''}`}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  style={{ display: 'none' }}
                  onChange={e => handleFile(e.target.files[0])}
                />
                <Upload size={28} />
                <span>{fileName || 'Drop .csv file here or click to browse'}</span>
              </div>
            </div>

            {/* Instructions */}
            <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', lineHeight: 1.6 }}>
              Upload the RSLogix 5000 CSV processed through Tool 2 (UDT Comment Generation).
              All COMMENT rows tagged <code>&lt;HMI&gt;</code> will be included in the output.
              TagXRef entries must be committed to the database before generating.
            </p>
          </div>

          {/* ═══════════ TILE 2: GENERATE ═══════════ */}
          <div className="import-step-panel" style={{ maxWidth: '360px' }}>
            <div className="import-step-header">
              <span className="import-step-number">2</span>
              <h3>Generate</h3>
            </div>

            {/* Outputs description */}
            <div style={{
              background: 'var(--color-gray-50)',
              border: '1px solid var(--color-gray-200)',
              borderRadius: '4px',
              padding: '10px 12px',
              fontSize: 'var(--text-xs)',
              color: 'var(--color-gray-600)',
              lineHeight: 1.6,
              marginBottom: 'var(--spacing-md)'
            }}>
              <strong>Outputs ({fileCount} files):</strong>
              <ul style={{ margin: '4px 0 0', paddingLeft: '16px' }}>
                {fileLabels.map(l => <li key={l}>{l}</li>)}
              </ul>
            </div>

            <button
              className="import-execute-btn"
              onClick={handleGenerate}
              disabled={generating || !file}
              style={{ width: '100%', marginBottom: 'var(--spacing-md)' }}
            >
              {generating
                ? <><Loader size={16} className="spinning" style={{ marginRight: '6px' }} />Generating...</>
                : <><Radio size={16} style={{ marginRight: '6px' }} />Generate SCADA Tags</>
              }
            </button>

            {generateError && (
              <div className="import-val-result fail">
                <div className="import-val-summary">
                  <AlertTriangle size={14} />
                  <span>{generateError}</span>
                </div>
              </div>
            )}

            {generateResult && (
              <div className="import-val-result pass">
                <div className="import-val-summary">
                  <CheckCircle size={18} />
                  <span>Complete &mdash; {fileCount} files downloading</span>
                </div>
                <div className="import-val-section">
                  <div style={{ fontSize: 'var(--text-xs)', lineHeight: 1.8 }}>
                    {system === 'CIM' ? (
                      <>
                        <div>HMI tags: <strong>{generateResult.hmiTagCount}</strong></div>
                        <div>DataLog rows: <strong>{generateResult.dataLogCount}</strong></div>
                        <div>Alarm Config tags: <strong>{generateResult.alarmConfigCount}</strong></div>
                      </>
                    ) : (
                      <>
                        <div>First Pass Root Tags: <strong>{generateResult.ignFirstPassCount}</strong></div>
                        <div>2nd Pass Tags: <strong>{generateResult.hmiTagCount}</strong></div>
                        <div>Alarm Config Tags: <strong>{generateResult.alarmConfigCount}</strong></div>
                      </>
                    )}
                  </div>
                  <div style={{ marginTop: '6px', borderTop: '1px solid var(--color-gray-200)', paddingTop: '6px', fontSize: 'var(--text-xs)' }}>
                    {[
                      { name: generateResult.fileNameA },
                      { name: generateResult.fileNameB },
                      ...(generateResult.fileC ? [{ name: generateResult.fileNameC }] : [])
                    ].map(({ name }) => (
                      <div key={name} style={{ wordBreak: 'break-all' }}>&#8595; {name}</div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      <style>{`
        .spinning { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

// ─── Shared styles ────────────────────────────────────────────────────────────

const labelStyle = {
  fontSize: 'var(--text-sm)', fontWeight: 600,
  color: 'var(--color-gray-700)', display: 'block', marginBottom: '6px'
};

const inputStyle = {
  width: '100%', padding: '7px 10px',
  border: '1px solid var(--color-gray-300)', borderRadius: '4px',
  fontSize: 'var(--text-sm)', color: 'var(--color-gray-800)',
  background: 'var(--color-bg-white)'
};
